import React, { useEffect, useState } from 'react';
import { formatCompactCurrency } from '../utils/formatters';
import api from '../services/api';

interface CostSavingsData {
  config: {
    companyName: string;
    currentEBITDA: number;
    fy26Target: number;
  };
  summary: {
    totalTarget: number;
    totalAchieved: number;
    achievement: number;
  };
  initiatives: Array<{
    id: string;
    name: string;
    target: number;
    achieved: number;
    status: string;
    category?: string;
    owner?: string;
    notes?: string;
  }>;
}

export const CostSavingsDashboard: React.FC = () => {
  const [data, setData] = useState<CostSavingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/dashboard/cost-savings');
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load cost savings data');
        }
      } catch (err) {
        setError('Failed to load cost savings data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  const getConfidenceBadgeClass = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('high') || statusLower.includes('completed') || statusLower.includes('on_track'))
      return 'bg-success text-white';
    if (statusLower.includes('medium') || statusLower.includes('progress'))
      return 'bg-warning text-black';
    if (statusLower.includes('low') || statusLower.includes('risk') || statusLower.includes('delayed'))
      return 'bg-danger text-white';
    return 'bg-primary text-white';
  };

  const totalSavings = data.initiatives.reduce((sum, init) => sum + init.target, 0);

  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b-4 border-primary">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-primary mb-2">
              Cost Savings Dashboard
            </h1>
            <p className="text-lg text-text-secondary">
              EBITDA Impact Initiatives - Path to EBITDA Target
            </p>
            <p className="text-sm text-text-muted mt-2">
              Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Cost Savings Overview */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Cost Savings Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-background border-2 border-border rounded-lg p-6 text-center">
              <div className="text-sm text-primary uppercase mb-2 font-bold">Total EBITDA Savings</div>
              <div className="text-5xl font-bold text-primary my-3">
                {formatCompactCurrency(totalSavings)}
              </div>
              <div className="text-xs text-text-muted uppercase">Annual Run-Rate Impact</div>
            </div>
            <div className="bg-background border-2 border-border rounded-lg p-6 text-center">
              <div className="text-sm text-primary uppercase mb-2 font-bold">Active Initiatives</div>
              <div className="text-5xl font-bold text-primary my-3">
                {data.initiatives.length}
              </div>
              <div className="text-xs text-text-muted uppercase">Cost Reduction Programs</div>
            </div>
          </div>
        </section>

        {/* EBITDA Cost Savings Initiatives */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            EBITDA Cost Savings Initiatives
          </h2>
          <div className="space-y-6">
            {data.initiatives.map((initiative, index) => (
              <div
                key={initiative.id}
                className="border-2 border-primary rounded-lg p-6"
              >
                {/* Initiative Title */}
                <div className="text-primary text-2xl font-bold mb-5">
                  {index + 1}. {initiative.name} - {formatCompactCurrency(initiative.target)}
                </div>

                {/* Initiative Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5 text-center">
                  <div>
                    <div className="text-text-muted text-xs uppercase mb-2">Annual Savings</div>
                    <div className="text-primary text-xl font-bold">
                      {formatCompactCurrency(initiative.target)}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-muted text-xs uppercase mb-2">Confidence</div>
                    <div>
                      <span
                        className={`inline-block px-4 py-1.5 rounded-full font-bold text-xs ${getConfidenceBadgeClass(
                          initiative.status
                        )}`}
                      >
                        {initiative.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-text-muted text-xs uppercase mb-2">Category</div>
                    <div className="text-text-primary text-sm">
                      {initiative.category || 'Cost Savings'}
                    </div>
                  </div>
                </div>

                {/* Implementation Notes & Progress */}
                <div className="bg-background rounded p-5">
                  <div className="text-primary font-bold mb-3">Implementation Notes & Progress:</div>
                  <div className="bg-surface-light border border-border rounded p-4 min-h-[80px]">
                    <p className="text-text-secondary text-sm">
                      {initiative.notes || '[Add progress on cost savings implementation and timeline]'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* EBITDA Cost Savings Summary Table */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            EBITDA Cost Savings Summary
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-light border-b-2 border-primary">
                  <th className="text-left py-3 px-4 text-primary font-bold">Initiative</th>
                  <th className="text-right py-3 px-4 text-primary font-bold">Annual Savings</th>
                  <th className="text-center py-3 px-4 text-primary font-bold">Confidence</th>
                  <th className="text-left py-3 px-4 text-primary font-bold">Category</th>
                </tr>
              </thead>
              <tbody>
                {data.initiatives.map((initiative) => (
                  <tr
                    key={initiative.id}
                    className="border-b border-border hover:bg-surface-light transition-colors"
                  >
                    <td className="py-4 px-4">
                      <p className="font-medium text-text-primary">{initiative.name}</p>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <p className="font-bold text-primary">
                        {formatCompactCurrency(initiative.target)}
                      </p>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getConfidenceBadgeClass(
                          initiative.status
                        )}`}
                      >
                        {initiative.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-text-secondary">
                        {initiative.category || 'Cost Savings'}
                      </p>
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-surface-light border-t-2 border-primary font-bold">
                  <td className="py-4 px-4">
                    <p className="text-text-primary font-bold text-lg">TOTAL EBITDA IMPACT</p>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <p className="text-primary font-bold text-xl">
                      {formatCompactCurrency(totalSavings)}
                    </p>
                  </td>
                  <td colSpan={2} className="py-4 px-4">
                    <p className="text-text-secondary">Annual Run-Rate Savings</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Note about excluded savings */}
          <div className="mt-6 bg-background border-l-4 border-text-muted rounded p-4">
            <p className="text-text-muted text-sm italic">
              <strong>Note:</strong> Development cost savings excluded as cash-only savings with no EBITDA impact
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};
